import { supabase } from './supabaseClient';

export type WalletActivityOperationType = 'claim' | 'send' | 'swap';
export type WalletActivityLegType = 'approval' | 'claim' | 'transfer' | 'swap' | 'fee';

export type WalletActivityLeg = {
  leg_type: WalletActivityLegType;
  sequence_no?: number;
  chain_id?: number;
  tx_hash?: string | null;
  user_op_hash?: string | null;
  world_transaction_id?: string | null;
  from_address?: string | null;
  to_address?: string | null;
  token_address?: string | null;
  token_symbol?: string | null;
  amount_base_units?: string | null;
  amount_out_base_units?: string | null;
};

export function buildWalletOperationIdempotencyKey(
  operationType: WalletActivityOperationType,
  txHash: string,
): string {
  return `wallet:${operationType}:${txHash.trim().toLowerCase()}`;
}

export type BubbleWalletActivityType =
  | 'shop_purchase_bbg'
  | 'gacha_bbg'
  | 'furnace_bbt'
  | 'skin_purchase'
  | 'character_synthesis_bbg'
  | 'throne_challenge_bbg'
  | 'claim_bbg'
  | 'swap'
  | 'send';

export type BubbleWalletActivityRecord = {
  activityId: string;
  activityDomain: 'bubble_game' | 'bubble_wallet';
  activityType: BubbleWalletActivityType;
  direction: 'spend' | 'receive' | 'swap' | 'send';
  status: string;
  occurredAt: string;
  tokenSymbol: string | null;
  tokenSymbolOut: string | null;
  amount: string | null;
  amountOut: string | null;
  amountBaseUnits: string | null;
  amountOutBaseUnits: string | null;
  sourceTable: string;
  sourceId: string | null;
  ledgerEntryId: string | null;
  operationId: string | null;
  chainId: number | null;
  userOpHash: string | null;
  worldTransactionId: string | null;
  txHash: string | null;
  referenceType: string | null;
  referenceId: string | null;
  metadata: Record<string, unknown>;
};

export type BubbleWalletActivityCursor = {
  occurredAt: string;
  activityId: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function getNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getAmount(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return getString(value);
}

const activityTypes = new Set<BubbleWalletActivityType>([
  'shop_purchase_bbg',
  'gacha_bbg',
  'furnace_bbt',
  'skin_purchase',
  'character_synthesis_bbg',
  'throne_challenge_bbg',
  'claim_bbg',
  'swap',
  'send',
]);

const activityDomains = new Set<BubbleWalletActivityRecord['activityDomain']>([
  'bubble_game',
  'bubble_wallet',
]);

const activityDirections = new Set<BubbleWalletActivityRecord['direction']>([
  'spend',
  'receive',
  'swap',
  'send',
]);

export function parseBubbleWalletActivityRows(value: unknown): BubbleWalletActivityRecord[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((row): BubbleWalletActivityRecord[] => {
    if (!isRecord(row)) return [];

    const activityId = getString(row.activity_id);
    const activityDomain = getString(row.activity_domain);
    const activityType = getString(row.activity_type);
    const direction = getString(row.direction);
    const status = getString(row.status);
    const occurredAt = getString(row.occurred_at);
    const sourceTable = getString(row.source_table);

    if (
      !activityId
      || !activityDomain
      || !activityTypes.has(activityType as BubbleWalletActivityType)
      || !activityDomains.has(activityDomain as BubbleWalletActivityRecord['activityDomain'])
      || !activityDirections.has(direction as BubbleWalletActivityRecord['direction'])
      || !status
      || !occurredAt
      || !sourceTable
    ) return [];

    return [{
      activityId,
      activityDomain: activityDomain as BubbleWalletActivityRecord['activityDomain'],
      activityType: activityType as BubbleWalletActivityType,
      direction: direction as BubbleWalletActivityRecord['direction'],
      status,
      occurredAt,
      tokenSymbol: getString(row.token_symbol),
      tokenSymbolOut: getString(row.token_symbol_out),
      amount: getAmount(row.amount),
      amountOut: getAmount(row.amount_out),
      amountBaseUnits: getString(row.amount_base_units),
      amountOutBaseUnits: getString(row.amount_out_base_units),
      sourceTable,
      sourceId: getString(row.source_id),
      ledgerEntryId: getString(row.ledger_entry_id),
      operationId: getString(row.operation_id),
      chainId: getNumber(row.chain_id),
      userOpHash: getString(row.user_op_hash),
      worldTransactionId: getString(row.world_transaction_id),
      txHash: getString(row.tx_hash),
      referenceType: getString(row.reference_type),
      referenceId: getString(row.reference_id),
      metadata: isRecord(row.metadata) ? row.metadata : {},
    }];
  });
}

export async function getBubbleWalletSpendingActivity(
  limit = 100,
  cursor?: BubbleWalletActivityCursor,
): Promise<BubbleWalletActivityRecord[]> {
  if (!supabase) throw new Error('Supabase 尚未設定，無法讀取 Bubble Wallet 活動。');

  const { data, error } = await supabase.schema('api').rpc(
    'get_my_bubble_wallet_spending_activity_v1',
    {
      p_limit: limit,
      p_before_occurred_at: cursor?.occurredAt ?? null,
      p_before_activity_id: cursor?.activityId ?? null,
    },
  );

  if (error) throw new Error(`Bubble Wallet 活動讀取失敗：${error.message}`);
  return parseBubbleWalletActivityRows(data);
}

export type RecordWalletOperationRequest = {
  operation_type: WalletActivityOperationType;
  idempotency_key: string;
  wallet_address: string;
  reference_type?: string | null;
  reference_id?: string | null;
  metadata?: Record<string, unknown> | null;
  legs: WalletActivityLeg[];
};

export async function recordWalletOperation(
  request: RecordWalletOperationRequest,
): Promise<string> {
  if (!supabase) throw new Error('Supabase 尚未設定，無法記錄錢包活動。');

  const { data, error } = await supabase.functions.invoke('record-wallet-operation', {
    body: request,
  });

  if (error) throw new Error(`錢包活動記錄失敗：${error.message}`);
  if (!data || data.success !== true || typeof data.operation_id !== 'string') {
    throw new Error(
      typeof data?.message === 'string' ? data.message : '錢包活動記錄失敗。',
    );
  }

  return data.operation_id;
}

export async function recordWalletOperationBestEffort(
  request: RecordWalletOperationRequest,
): Promise<string | null> {
  try {
    return await recordWalletOperation(request);
  } catch (error) {
    // The chain receipt remains authoritative. A caller may retry this request
    // with the same tx-derived idempotency key without creating another row.
    console.error('[walletActivity] record operation failed:', error);
    return null;
  }
}
